import dayjs from "dayjs";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useShallow } from "zustand/react/shallow";
import { TimeOffForm } from "@/pages/Dashboard/Schedule/Components/TimeOff/form/TimeOffForm";
import { useTimeOffForm } from "@/pages/Dashboard/Schedule/Components/TimeOff/form/useTimeOffForm";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { useCalendarStore } from "../../store/useCalendarStore";

/** The drawer's "Odsotnost" tab, opened on what was clicked; the drawer owns Save. */
const BreakModal = forwardRef(function BreakModal(
  props: {
    onClose: () => void;
    onValidChange: (value: boolean) => void;
  },
  ref,
) {
  const {
    selectedLocationId,
    lastSelectedUserId,
    lastSelectedResourceId,
    lastSelectedDate,
    lastSelectedTime,
    selectedTimeOff,
    newBreakData,
    users,
    resources,
  } = useCalendarStore(
    useShallow((state) => ({
      selectedLocationId: state.selectedLocationId,
      lastSelectedUserId: state.lastSelectedUserId,
      lastSelectedResourceId: state.lastSelectedResourceId,
      lastSelectedDate: state.lastSelectedDate,
      lastSelectedTime: state.lastSelectedTime,
      selectedTimeOff: state.selectedTimeOff,
      newBreakData: state.newBreakData,
      users: state.users,
      resources: state.resources,
    })),
  );

  const permissions = useUserPermissionsStore(
    (state: { permissions: Record<string, unknown> }) => state.permissions,
  );

  // Null unless a slot was clicked, and an unparseable date would reach the endpoint.
  const date = lastSelectedDate
    ? dayjs(lastSelectedDate).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  const state = useTimeOffForm({
    locationId: selectedLocationId,
    people: users,
    initial: {
      date,
      startTime: lastSelectedTime
        ? dayjs(lastSelectedTime).format("HH:mm")
        : undefined,
      userId: lastSelectedUserId,
      resourceId: lastSelectedResourceId,
      granularity: newBreakData?.granularity,
    },
    editing:
      selectedTimeOff != null
        ? { target: selectedTimeOff.source, id: selectedTimeOff.id }
        : null,
    // The same check `/time-off/over-shifts` makes.
    allowDayTypes: !!permissions.view_availability,
  });

  useImperativeHandle(ref, () => ({
    async action(refetchTimeoffs: () => void) {
      if (await state.save()) {
        refetchTimeoffs();
        props.onClose();
      }
    },
  }));

  useEffect(() => {
    props.onValidChange(state.canSave);
  }, [state.canSave]);

  return (
    <TimeOffForm
      state={state}
      resources={resources}
      locationId={selectedLocationId}
      canManageTypes={!!permissions.manage_timeoff_types}
      onDone={props.onClose}
    />
  );
});
BreakModal.displayName = "BreakModal";

export default BreakModal;
